import React, { useMemo, useState } from 'react'
import { Button, List } from 'antd'
import styled from 'styled-components'
import { truncateText } from 'utils/ExpensiveFunction'
import { AnnouncementDelete, AnnouncementUpdate } from './AnnouncementActions'
import AdminEventImages from '../../EventImages'
import EntryTime from '../../../../components/PositionColumns/EntryTime'
import { MarkdownComponent } from 'app/components'
import { PushpinFilled } from '@ant-design/icons'

const PREVIEW_LENGTH = 420

const ItemBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  width: 100%;
  color: #1f2937;
`

const Header = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
`

const TitleRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
`

const FixedBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 12px 4px 10px;
  border-radius: 999px;
  background: rgba(255, 196, 115, 0.22);
  color: #b45309;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.02em;
`

const Title = styled.h3`
  margin: 0;
  font-size: 20px;
  font-weight: 700;
  letter-spacing: -0.01em;
  color: #0f172a;
  word-break: keep-all;
`

const Meta = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
  color: #6b7280;
  font-size: 13px;
`

const MetaChip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  border-radius: 999px;
  background: #eef2ff;
  color: #4f46e5;
  font-weight: 600;
  font-size: 12px;
`

const ContentSection = styled.div<{ hasMedia: boolean }>`
  display: grid;
  grid-template-columns: ${({ hasMedia }) => (hasMedia ? 'minmax(0, 1fr) minmax(180px, 240px)' : 'minmax(0, 1fr)')};
  gap: ${({ hasMedia }) => (hasMedia ? '20px' : '0')};
  align-items: flex-start;

  @media (max-width: 992px) {
    grid-template-columns: 1fr;
    gap: 18px;
  }
`

const RichContent = styled.div<{ collapsed: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 14px;
  color: #4b5563;
  font-size: 15px;
  line-height: 1.68;
  ${({ collapsed }) =>
		collapsed &&
		`
      overflow: hidden;
    `}

  & p {
    margin: 0;
  }

  & a {
    color: #2563eb;
  }
`

const MediaContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f8faff;
  border: 1px solid #e2e8f8;
  border-radius: 16px;
  padding: 12px;
  min-height: 160px;
`

const ActionRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 4px;
`

const ToggleButton = styled(Button)`
  padding: 0;
  height: auto;
  color: #2563eb;
  font-weight: 600;
  font-size: 13px;

  &:hover,
  &:focus {
    color: #1d4ed8;
    background: transparent;
  }
`

const AdminAnnouncementItems = ({ item }) => {
	const rawContent = item?.content ?? ''
	const [expanded, setExpanded] = useState(rawContent.length <= PREVIEW_LENGTH)

	const images = useMemo(() => {
		try {
			const parsed = JSON.parse(item?.images ?? '[]')
			return Array.isArray(parsed) ? parsed : []
		} catch (error) {
			return []
		}
	}, [item?.images])

	const isLongContent = rawContent.length > PREVIEW_LENGTH
	const displayContent = expanded || !isLongContent
		? rawContent
		: `${rawContent.slice(0, PREVIEW_LENGTH).trimEnd()}...`

	return (
		<List.Item
			key={item.title}>
			<ItemBody>
				<Header>
					<TitleRow>
						{item?.is_fixed ? (
							<FixedBadge>
								<PushpinFilled style={{ fontSize: 14 }} />
								고정 공지
							</FixedBadge>
						) : null}
						<Title title={item?.title}>{truncateText(item?.title ?? '', 140)}</Title>
					</TitleRow>
					<Meta>
						<EntryTime
							time={item?.rdate}
							style={{
								fontSize: 13,
								color: 'inherit',
								letterSpacing: 0,
								fontWeight: 500,
							}}
						/>
						{images.length ? <MetaChip>이미지 {images.length}개</MetaChip> : null}
					</Meta>
				</Header>
				<ContentSection hasMedia={images.length > 0}>
					<RichContent collapsed={!expanded && isLongContent}>
						<MarkdownComponent markdown={displayContent} />
						{isLongContent ? (
							<ToggleButton type="link" onClick={() => setExpanded(!expanded)}>
								{expanded ? '내용 접기' : '자세히 보기'}
							</ToggleButton>
						) : null}
					</RichContent>
					{images.length ? (
						<MediaContainer>
							<AdminEventImages images={images} />
						</MediaContainer>
					) : null}
				</ContentSection>
				<ActionRow>
					<AnnouncementUpdate item={item} />
					<AnnouncementDelete item={item} />
				</ActionRow>
			</ItemBody>
		</List.Item>
	)
}

export default AdminAnnouncementItems
